// Browser audio recording with MediaRecorder.
//
// Requirements for the implementation:
//  * MIME selection order: audio/webm;codecs=opus, audio/mp4 (iOS Safari), audio/ogg;codecs=opus, audio/webm, '' (browser default).
//  * Profiles: 'meeting' (room microphone, several speakers: echoCancellation=false, noiseSuppression=false,
//    autoGainControl=true, channelCount=1) and 'dictation' (close talking: all three true). ~32 kbps.
//  * Persist chunks to IndexedDB as they arrive (timeslice 5 s meeting, 1 s dictation) under a recovery id,
//    so an interrupted recording can be recovered after a crash/reload. stop() deletes the persisted chunks
//    only after the caller confirms via `discardRecording(recoveryId)` OR immediately when persist=false.
//  * Screen Wake Lock while recording (re-acquire on visibilitychange), released on stop/cancel.
//  * Level meter: RMS of an AnalyserNode, 0..1, smoothed; exposed via `level` getter (poll with rAF).
//  * Elapsed time excludes paused time.
//  * If the audio track ends unexpectedly (device unplugged, iOS interruption) emit 'interrupted' and stop
//    gracefully, keeping what was recorded.
//  * A muted track (iOS mutes the capture during a phone call or Siri instead of ending it) emits 'muted';
//    `muted` tells the UI that silence is being recorded until the track is unmuted.
//  * `prepare()` acquires the microphone without recording (used by the inspection page to make each
//    dictation clip start instantly); `release()` stops the tracks. start() calls prepare() if needed.
//
// Microphone lifetime: after prepare() the microphone stays open across recordings until release(),
// except while the page is hidden (the Android app or the browser in the background, another tab)
// or the Android app records itself: a prepared microphone that is not recording is closed then,
// and the next start() opens it again. When start() had to open it itself, stop()/cancel() close
// it again, so the browser's "microphone in use" indicator does not stay on after a meeting.
//
// Inside the Android app only one recording runs at a time (lib/android.ts): while the app records
// (widget, "Snemanje" panel) no microphone is opened here (MSG_NATIVE_RECORDING), and the app is
// told while a recording runs here, so that it refuses to start one of its own meanwhile.
//
// A recording session holds its recovery record (localdb.holdRecording: in this tab and, through a
// Web Lock, for other tabs) and refreshes its `updatedAt` every few seconds, also while paused, so no
// other tab offers a running recording for recovery, saves it or deletes it.
import {
  deleteRecordingData,
  getRecordingChunks,
  getRecordingRecord,
  holdRecording,
  isRecordingHeldHere,
  listRecordingRecords,
  recordingsInUse,
  rememberRecordingBlob,
  reportStorageError,
  saveRecordingChunk,
  type RecordingRecord,
} from './localdb';
import { isNativeRecording, NATIVE_RECORDING_EVENT, reportRecordingToApp } from './android';
import { discardRecovery, isParkedId, parkedUpload, parkedUploads, queuedRecoveryIds } from './outbox';

export type RecorderProfile = 'meeting' | 'dictation';
export type RecorderState = 'idle' | 'preparing' | 'ready' | 'recording' | 'paused' | 'stopping';

export interface RecorderOptions {
  profile: RecorderProfile;
  /** Persist chunks to IndexedDB for crash recovery (default true). */
  persist?: boolean;
  /** Arbitrary data stored with the recovery record (e.g. { docId, clipId, type }). */
  recoveryMeta?: Record<string, unknown>;
}

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  /** Id of the persisted recovery record (call discardRecording(id) once the blob is safely queued). */
  recoveryId: string | null;
  startedAt: string;
}

export interface RecoverableRecording {
  id: string;
  startedAt: string;
  updatedAt: string;
  mimeType: string;
  /** Approximate duration (sum of chunk timestamps). */
  durationMs: number;
  bytes: number;
  profile: RecorderProfile;
  meta: Record<string, unknown>;
}

export type RecorderEventType = 'state' | 'error' | 'interrupted' | 'muted';

export const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'] as const;
export const AUDIO_BITS_PER_SECOND = 32_000;
export const TIMESLICE_MS: Record<RecorderProfile, number> = { meeting: 5_000, dictation: 1_000 };
/** Safety net for browsers that never fire 'stop' after an error. */
const STOP_TIMEOUT_MS = 4_000;
/** Records without data younger than this may belong to a recording just started in another tab. */
const EMPTY_RECORD_GRACE_MS = 60_000;
/**
 * How often a running (or paused) session refreshes its record's `updatedAt`. Well below the
 * recovery dialog's 20 s "probably active in another tab" window, for browsers without Web Locks.
 */
export const HEARTBEAT_MS = 5_000;

/** The Android app records itself (widget, "Snemanje" panel): the page does not open the microphone. */
export const MSG_NATIVE_RECORDING =
  'Snemanje s pripomočkom že teče. Najprej ga ustavite (na pripomočku, v obvestilu ali v oknu Snemanje), nato poskusite znova.';

/** Shown while the microphone track is muted (e.g. a phone call on iOS). */
export const MSG_MUTED =
  'Mikrofon je začasno nedosegljiv (npr. zaradi klica). Snemanje se nadaljuje, ko bo spet na voljo.';

const MSG_UNSUPPORTED =
  'Ta brskalnik ne podpira snemanja zvoka. Uporabite novejši Chrome, Safari, Edge ali Firefox; stran mora biti odprta prek varne povezave (HTTPS).';
const MSG_DENIED =
  'Dostop do mikrofona ni dovoljen. Dovolite uporabo mikrofona v nastavitvah brskalnika in poskusite znova.';
const MSG_NOT_FOUND = 'Mikrofona ni bilo mogoče najti. Preverite, ali je mikrofon priključen.';
const MSG_BUSY = 'Mikrofona ni mogoče uporabiti. Morda ga uporablja druga aplikacija (npr. klic).';
const MSG_GENERIC = 'Mikrofona ni bilo mogoče vklopiti.';
const MSG_RECORDER = 'Snemanja ni bilo mogoče začeti.';
const MSG_ALREADY = 'Snemanje že poteka.';
const MSG_NOT_RECORDING = 'Snemanje ne poteka.';
const MSG_RELEASED = 'Snemanje je bilo prekinjeno.';
const MSG_IN_USE = 'Posnetek je še v uporabi (snemanje ali pošiljanje še poteka). Poskusite znova pozneje.';
const MSG_RECOVERY_MISSING = 'Posnetka za obnovitev ni bilo mogoče najti.';

/** MediaRecorder + getUserMedia available (and secure context). */
export function isRecordingSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  // getUserMedia only exists in secure contexts (HTTPS or localhost).
  if (globalThis.isSecureContext === false) return false;
  return typeof MediaRecorder !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/** First supported MIME type of MIME_CANDIDATES, or '' for the browser default. */
export function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  for (const type of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type;
    } catch {
      // Some engines throw for unknown types instead of returning false.
    }
  }
  return '';
}

export function audioConstraints(profile: RecorderProfile): MediaTrackConstraints {
  // channelCount is a plain (ideal) value so devices that cannot do mono still work.
  return profile === 'meeting'
    ? { echoCancellation: false, noiseSuppression: false, autoGainControl: true, channelCount: 1 }
    : { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 };
}

function errorName(err: unknown): string {
  return typeof err === 'object' && err !== null && 'name' in err ? String((err as { name: unknown }).name) : '';
}

function microphoneErrorMessage(err: unknown): string {
  switch (errorName(err)) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return MSG_DENIED;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return MSG_NOT_FOUND;
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return isNativeRecording() ? MSG_NATIVE_RECORDING : MSG_BUSY;
    default:
      return MSG_GENERIC;
  }
}

async function openMicrophone(profile: RecorderProfile): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: audioConstraints(profile), video: false });
  } catch (err) {
    // Old engines reject unknown constraints; retry with the plainest request.
    if (errorName(err) === 'OverconstrainedError' || err instanceof TypeError) {
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    }
    throw err;
  }
}

function isLive(stream: MediaStream | null): stream is MediaStream {
  return !!stream && stream.getAudioTracks().some((t) => t.readyState === 'live');
}

/** Every audio track is muted by the system (it stays 'live', but delivers silence). */
function isMuted(stream: MediaStream | null): boolean {
  const tracks = stream?.getAudioTracks() ?? [];
  return tracks.length > 0 && tracks.every((t) => t.muted === true);
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

function waitFor(promise: Promise<void>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    void promise.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/** One MediaRecorder run from start() to stop()/cancel(). */
interface Session {
  mr: MediaRecorder;
  mimeType: string;
  chunks: Blob[];
  bytes: number;
  nextIndex: number;
  recoveryId: string | null;
  startedAt: string;
  meta: Record<string, unknown>;
  /** Recorded time before the current run (pauses excluded). */
  activeMs: number;
  /** now() when recording last (re)started; null while paused or stopped. */
  resumedAt: number | null;
  /** Resolves on the recorder's 'stop' event (after the final 'dataavailable'). */
  stopped: Promise<void>;
  /** Serialised IndexedDB writes of this session. */
  persistChain: Promise<void>;
  discarded: boolean;
  /** Releases the hold on the recovery record (see localdb.holdRecording). */
  unhold: (() => void) | null;
  /** Refreshes the record's updatedAt while the session exists (see HEARTBEAT_MS). */
  heartbeat: ReturnType<typeof setInterval> | null;
}

/** Recording sessions of every recorder on this page; the Android app is told whether there is one. */
const liveSessions = new Set<Session>();

function setSessionLive(s: Session, live: boolean): void {
  const before = liveSessions.size > 0;
  if (live) liveSessions.add(s);
  else liveSessions.delete(s);
  const after = liveSessions.size > 0;
  if (after !== before) reportRecordingToApp(after);
}

export class AudioRecorder extends EventTarget {
  readonly profile: RecorderProfile;
  private readonly persist: boolean;
  private readonly baseMeta: Record<string, unknown>;

  private _state: RecorderState = 'idle';
  private _error: string | null = null;
  private _muted = false;
  private stream: MediaStream | null = null;
  private preparing: Promise<void> | null = null;
  /** True after an explicit prepare(): keep the microphone open between recordings. */
  private warm = false;
  /** Bumped by release() so a getUserMedia that resolves afterwards is discarded. */
  private generation = 0;
  /** start() calls waiting for the microphone (an idle check must not close it under them). */
  private starting = 0;

  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private samples: Float32Array<ArrayBuffer> | null = null;
  private smoothedLevel = 0;

  private session: Session | null = null;
  private stopPromise: Promise<RecordingResult> | null = null;
  private lastElapsedMs = 0;
  private wakeLock: WakeLockSentinel | null = null;
  private wakeLockPending = false;

  constructor(options: RecorderOptions) {
    super();
    this.profile = options.profile;
    this.persist = options.persist !== false;
    this.baseMeta = { ...options.recoveryMeta };
  }

  get state(): RecorderState {
    return this._state;
  }

  get elapsedMs(): number {
    const s = this.session;
    if (!s) return this.lastElapsedMs;
    return s.activeMs + (s.resumedAt !== null ? now() - s.resumedAt : 0);
  }

  /** Current input level 0..1. */
  get level(): number {
    const analyser = this.analyser;
    if (!analyser || !isLive(this.stream)) return 0;
    const size = analyser.fftSize;
    if (!this.samples || this.samples.length !== size) this.samples = new Float32Array(size);
    const buf = this.samples;
    if (typeof analyser.getFloatTimeDomainData === 'function') {
      analyser.getFloatTimeDomainData(buf);
    } else {
      const bytes = new Uint8Array(size);
      analyser.getByteTimeDomainData(bytes);
      for (let i = 0; i < size; i++) buf[i] = (bytes[i]! - 128) / 128;
    }
    let sum = 0;
    for (let i = 0; i < size; i++) sum += buf[i]! * buf[i]!;
    const rms = Math.sqrt(sum / size);
    // Map -60 dBFS..-6 dBFS to 0..1: speech then moves the meter visibly without clipping it.
    const db = rms > 0 ? 20 * Math.log10(rms) : -100;
    const target = Math.min(1, Math.max(0, (db + 60) / 54));
    // Fast attack, slow release (like a VU meter), tuned for ~60 polls per second.
    const k = target > this.smoothedLevel ? 0.5 : 0.12;
    this.smoothedLevel += (target - this.smoothedLevel) * k;
    return this.smoothedLevel;
  }

  /** Slovenian error message of the last failure (permission denied, no microphone, unsupported...). */
  get error(): string | null {
    return this._error;
  }

  /**
   * True while the open microphone track is muted by the system (iOS: phone call, Siri): the
   * recording goes on but captures silence (see MSG_MUTED). Changes emit 'muted'.
   */
  get muted(): boolean {
    return this._muted;
  }

  /** Open the microphone without recording; it stays open until release() (see "Microphone lifetime"). */
  async prepare(): Promise<void> {
    this.warm = true;
    document.addEventListener('visibilitychange', this.onIdleCheck);
    window.addEventListener(NATIVE_RECORDING_EVENT, this.onIdleCheck);
    // Often called from a tap: create the AudioContext while the user gesture is still active.
    this.ensureAudioContext();
    await this.openStream();
  }

  async start(recoveryMeta?: Record<string, unknown>): Promise<void> {
    // Everything before the first await runs inside the caller's user gesture. iOS Safari only
    // lets an AudioContext start (resume) there.
    this.ensureAudioContext();
    if (this.session) throw new Error(MSG_ALREADY);
    // A prepared microphone that came back muted (iOS after a call or a locked screen) would only
    // record silence: open it again.
    if (!this.preparing && isMuted(this.stream)) this.closeStream();
    this.starting++;
    try {
      await this.openStream();
    } finally {
      this.starting--;
    }
    const stream = this.stream;
    if (!isLive(stream)) throw new Error(this._error ?? MSG_RELEASED);
    if (this.session) throw new Error(MSG_ALREADY);
    this.connectAnalyser();

    const preferred = pickMimeType();
    let mr: MediaRecorder;
    try {
      mr = new MediaRecorder(stream, {
        ...(preferred ? { mimeType: preferred } : {}),
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch {
      try {
        // Some engines reject the options object; the defaults still record fine.
        mr = new MediaRecorder(stream);
      } catch (err) {
        throw this.abortStart(err);
      }
    }

    const session: Session = {
      mr,
      mimeType: '',
      chunks: [],
      bytes: 0,
      nextIndex: 0,
      recoveryId: this.persist ? crypto.randomUUID() : null,
      startedAt: new Date().toISOString(),
      meta: { ...this.baseMeta, ...recoveryMeta },
      activeMs: 0,
      resumedAt: null,
      stopped: new Promise<void>((resolve) => mr.addEventListener('stop', () => resolve(), { once: true })),
      persistChain: Promise.resolve(),
      discarded: false,
      unhold: null,
      heartbeat: null,
    };
    mr.addEventListener('dataavailable', (e) => this.onData(session, e));
    mr.addEventListener('error', (e) => {
      console.error('[recorder] MediaRecorder error', e);
      this.interrupt(session);
    });

    try {
      mr.start(TIMESLICE_MS[this.profile]);
    } catch (err) {
      throw this.abortStart(err);
    }
    // Safari only reports the actual container after start().
    session.mimeType = mr.mimeType || preferred;
    session.resumedAt = now();
    this.session = session;
    setSessionLive(session, true);
    this.lastElapsedMs = 0;
    this._error = null;
    if (session.recoveryId) {
      session.unhold = holdRecording(session.recoveryId);
      this.persistRecord(session);
      // A paused recorder writes no chunks: keep the record visibly alive anyway.
      session.heartbeat = setInterval(() => this.persistRecord(session), HEARTBEAT_MS);
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.acquireWakeLock();
    this.setState('recording');
  }

  pause(): void {
    const s = this.session;
    if (!s || this._state !== 'recording') return;
    try {
      s.mr.pause();
    } catch (err) {
      console.warn('[recorder] pause failed', err);
      return;
    }
    this.freeze(s);
    // Flush what we have, so a crash during a long break loses nothing.
    try {
      s.mr.requestData();
    } catch {
      // Not every engine allows requestData() while paused.
    }
    this.setState('paused');
  }

  resume(): void {
    const s = this.session;
    if (!s || this._state !== 'paused') return;
    try {
      s.mr.resume();
    } catch (err) {
      console.warn('[recorder] resume failed', err);
      return;
    }
    s.resumedAt = now();
    void this.acquireWakeLock();
    this.setState('recording');
  }

  /**
   * Finish the recording and return it as one Blob. Also works after an interruption
   * (state 'stopping'). Persisted chunks are kept until discardRecording(recoveryId).
   */
  stop(): Promise<RecordingResult> {
    if (this.stopPromise) return this.stopPromise;
    const s = this.session;
    if (!s) return Promise.reject(new Error(MSG_NOT_RECORDING));
    const promise = this.finish(s).finally(() => {
      if (this.stopPromise === promise) this.stopPromise = null;
    });
    this.stopPromise = promise;
    return promise;
  }

  /** Stop and throw the recording away (also deletes persisted chunks). */
  async cancel(): Promise<void> {
    if (this.stopPromise) {
      const result = await this.stopPromise.catch(() => null);
      if (result?.recoveryId) await discardRecording(result.recoveryId).catch(reportStorageError);
      this.lastElapsedMs = 0;
      return;
    }
    const s = this.session;
    if (!s) return;
    s.discarded = true;
    stopHeartbeat(s);
    this.freeze(s);
    this.setState('stopping');
    this.stopRecorder(s, false);
    await waitFor(s.stopped, STOP_TIMEOUT_MS);
    await s.persistChain;
    if (s.recoveryId) await deleteRecordingData(s.recoveryId).catch(reportStorageError);
    this.lastElapsedMs = 0;
    this.endSession(s);
  }

  /**
   * Stop the microphone tracks and audio context. A recording still running is stopped, but its
   * persisted chunks are kept, so it is offered for recovery (e.g. the page was left mid-meeting).
   */
  release(): void {
    this.generation++;
    this.warm = false;
    const s = this.session;
    if (s && !this.stopPromise) {
      stopHeartbeat(s);
      this.freeze(s);
      this.stopRecorder(s, false);
      // The final chunk arrives asynchronously; keep the record held until it is written.
      void waitFor(s.stopped, STOP_TIMEOUT_MS)
        .then(() => s.persistChain)
        .finally(() => {
          s.unhold?.();
          s.unhold = null;
        });
      this.session = null;
      setSessionLive(s, false);
    }
    this.releaseWakeLock();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('visibilitychange', this.onIdleCheck);
    window.removeEventListener(NATIVE_RECORDING_EVENT, this.onIdleCheck);
    this.closeStream();
    if (this.ctx) {
      void this.ctx.close().catch(() => {});
      this.ctx = null;
    }
    this.smoothedLevel = 0;
    this.setState('idle');
  }

  // ---------- internals ----------

  private setState(state: RecorderState): void {
    if (this._state === state) return;
    this._state = state;
    this.dispatchEvent(new Event('state'));
  }

  /** start() failed after the microphone was opened: close it again unless it was prepared. */
  private abortStart(cause: unknown): Error {
    if (!this.warm) this.closeStream();
    this.setState(isLive(this.stream) ? 'ready' : 'idle');
    return this.fail(MSG_RECORDER, cause);
  }

  /** Remember a user-facing error, notify listeners and return an Error to throw. */
  private fail(message: string, cause?: unknown): Error {
    if (cause !== undefined) console.warn('[recorder]', message, cause);
    this._error = message;
    this.dispatchEvent(new Event('error'));
    return new Error(message);
  }

  private ensureAudioContext(): void {
    const scope = globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    // Safari < 14.1 only has the prefixed constructor.
    const Ctor = scope.AudioContext ?? scope.webkitAudioContext;
    if (!Ctor) return;
    try {
      if (!this.ctx || this.ctx.state === 'closed') {
        this.ctx = new Ctor();
        this.source = null;
        this.analyser = null;
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    } catch (err) {
      // The level meter is optional; recording works without it.
      console.warn('[recorder] AudioContext unavailable', err);
      this.ctx = null;
    }
  }

  private connectAnalyser(): void {
    if (!this.ctx || !this.stream || this.source) return;
    try {
      this.source = this.ctx.createMediaStreamSource(this.stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      // Not connected to the destination: we only measure, never play back (no feedback).
      this.source.connect(this.analyser);
    } catch (err) {
      console.warn('[recorder] level meter unavailable', err);
      this.source = null;
      this.analyser = null;
    }
  }

  private openStream(): Promise<void> {
    if (isLive(this.stream)) {
      if (this._state === 'idle') this.setState('ready');
      this.connectAnalyser();
      return Promise.resolve();
    }
    if (this.preparing) return this.preparing;
    const promise = this.acquireStream().finally(() => {
      if (this.preparing === promise) this.preparing = null;
    });
    this.preparing = promise;
    return promise;
  }

  private async acquireStream(): Promise<void> {
    if (!isRecordingSupported()) throw this.fail(MSG_UNSUPPORTED);
    // Never a second recording beside the Android app's own (it would fail or compete for the microphone).
    if (isNativeRecording()) throw this.fail(MSG_NATIVE_RECORDING);
    const generation = this.generation;
    if (!this.session) this.setState('preparing');
    let stream: MediaStream;
    try {
      stream = await openMicrophone(this.profile);
    } catch (err) {
      if (!this.session) this.setState('idle');
      throw this.fail(microphoneErrorMessage(err), err);
    }
    if (generation !== this.generation) {
      // release() was called while the permission prompt was open.
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.closeStream();
    this.stream = stream;
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', this.onTrackEnded);
      track.addEventListener('mute', this.onTrackMute);
      track.addEventListener('unmute', this.onTrackMute);
    }
    this._error = null;
    this.updateMuted();
    this.connectAnalyser();
    if (!this.session) this.setState('ready');
  }

  private closeStream(): void {
    const stream = this.stream;
    this.stream = null;
    if (stream) {
      for (const track of stream.getTracks()) {
        track.removeEventListener('ended', this.onTrackEnded);
        track.removeEventListener('mute', this.onTrackMute);
        track.removeEventListener('unmute', this.onTrackMute);
        track.stop();
      }
    }
    try {
      this.source?.disconnect();
    } catch {
      // Already disconnected.
    }
    this.source = null;
    this.analyser = null;
    this.updateMuted();
  }

  private onTrackMute = (): void => this.updateMuted();

  private updateMuted(): void {
    const muted = isMuted(this.stream);
    if (muted === this._muted) return;
    this._muted = muted;
    if (muted) console.warn('[recorder] microphone muted by the system');
    this.dispatchEvent(new Event('muted'));
  }

  private onTrackEnded = (): void => {
    const s = this.session;
    if (s && (this._state === 'recording' || this._state === 'paused')) {
      this.interrupt(s);
    } else if (!s && !isLive(this.stream)) {
      // The prepared microphone went away (unplugged, taken by a call): re-open on next start().
      this.closeStream();
      this.setState('idle');
    }
  };

  /** The system cut the recording short: keep the data and wait for the UI to call stop(). */
  private interrupt(s: Session): void {
    if (this.session !== s || this.stopPromise || this._state === 'stopping') return;
    this.freeze(s);
    this.setState('stopping');
    // Make the recorder deliver its last data and fire 'stop' (a no-op when it already stopped).
    this.stopRecorder(s, false);
    this.releaseWakeLock();
    this.dispatchEvent(new Event('interrupted'));
  }

  private freeze(s: Session): void {
    if (s.resumedAt !== null) {
      s.activeMs += now() - s.resumedAt;
      s.resumedAt = null;
    }
  }

  private stopRecorder(s: Session, requestFinal: boolean): void {
    if (s.mr.state === 'inactive') return;
    if (requestFinal) {
      try {
        s.mr.requestData();
      } catch {
        // stop() below delivers the final data anyway.
      }
    }
    try {
      s.mr.stop();
    } catch (err) {
      console.warn('[recorder] stop failed', err);
    }
  }

  private onData(s: Session, e: BlobEvent): void {
    const blob = e.data;
    if (!blob || blob.size === 0 || s.discarded) return;
    if (!s.mimeType && blob.type) s.mimeType = blob.type;
    s.chunks.push(blob);
    s.bytes += blob.size;
    if (s.recoveryId) {
      const chunk = { recordingId: s.recoveryId, index: s.nextIndex++, blob };
      this.persistRecord(s, chunk);
    }
  }

  private recordFor(s: Session): RecordingRecord {
    return {
      id: s.recoveryId!,
      startedAt: s.startedAt,
      updatedAt: new Date().toISOString(),
      mimeType: s.mimeType,
      profile: this.profile,
      meta: s.meta,
      durationMs: Math.round(s.activeMs + (s.resumedAt !== null ? now() - s.resumedAt : 0)),
      bytes: s.bytes,
    };
  }

  /** Queue a write of the recording record (+ chunk); writes stay in order, failures are reported. */
  private persistRecord(s: Session, chunk?: { recordingId: string; index: number; blob: Blob }): void {
    // cancel() deletes the data: nothing may write it again afterwards.
    if (s.discarded) return;
    const record = this.recordFor(s);
    s.persistChain = s.persistChain
      .then(() => saveRecordingChunk(record, chunk))
      .then(
        () => undefined,
        (err: unknown) => reportStorageError(err),
      );
  }

  private async finish(s: Session): Promise<RecordingResult> {
    stopHeartbeat(s);
    this.freeze(s);
    this.setState('stopping');
    this.stopRecorder(s, true);
    await waitFor(s.stopped, STOP_TIMEOUT_MS);
    // 'stop' follows the last 'dataavailable'; wait until those chunks are written as well.
    await s.persistChain;

    const mimeType = s.mimeType || s.chunks[0]?.type || 'audio/webm';
    const blob = new Blob(s.chunks, { type: mimeType });
    const durationMs = Math.round(s.activeMs);
    let recoveryId = s.recoveryId;
    if (recoveryId && s.chunks.length === 0) {
      // Nothing was recorded: there is nothing worth recovering.
      await deleteRecordingData(recoveryId).catch(reportStorageError);
      recoveryId = null;
    } else if (recoveryId) {
      this.persistRecord(s);
      await s.persistChain;
      // Lets api.addAudioClip link the queued upload to this crash copy (see OutboxOp.recoveryId).
      rememberRecordingBlob(blob, recoveryId);
    }
    // Set before endSession() emits 'state', so listeners read the final duration.
    this.lastElapsedMs = durationMs;
    this.endSession(s);
    return { blob, mimeType, durationMs, recoveryId, startedAt: s.startedAt };
  }

  private endSession(s: Session): void {
    stopHeartbeat(s);
    s.unhold?.();
    s.unhold = null;
    setSessionLive(s, false);
    if (this.session !== s) return; // release() already took over
    this.session = null;
    this.releaseWakeLock();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (!this.warm || !isLive(this.stream) || this.idleMustClose()) this.closeStream();
    this.setState(isLive(this.stream) ? 'ready' : 'idle');
  }

  /** A prepared microphone that is not recording must not stay open now (see "Microphone lifetime"). */
  private idleMustClose(): boolean {
    return pageHidden() || isNativeRecording();
  }

  /**
   * Page hidden or the Android app started a recording: close a prepared microphone that is not
   * recording (in the app's WebView it would also keep the phone in its call audio mode). Never
   * touches a recording; the next start() opens the microphone again.
   */
  private onIdleCheck = (): void => {
    if (!this.warm || this.session || this.starting > 0 || !this.idleMustClose()) return;
    const opening = this.preparing;
    if (opening) {
      // Still opening (e.g. the permission prompt hid the page): check again once it is open.
      void opening.then(this.onIdleCheck, () => {});
      return;
    }
    if (!this.stream) return;
    this.closeStream();
    this.setState('idle');
  };

  private onVisibilityChange = (): void => {
    // The browser drops the wake lock whenever the page is hidden.
    if (document.visibilityState === 'visible' && (this._state === 'recording' || this._state === 'paused')) {
      void this.acquireWakeLock();
    }
  };

  private async acquireWakeLock(): Promise<void> {
    const wakeLock = typeof navigator !== 'undefined' ? navigator.wakeLock : undefined;
    if (!wakeLock || this.wakeLock || this.wakeLockPending) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    this.wakeLockPending = true;
    try {
      const sentinel = await wakeLock.request('screen');
      if (!this.session || this._state === 'stopping') {
        void sentinel.release().catch(() => {});
        return;
      }
      this.wakeLock = sentinel;
      sentinel.addEventListener('release', () => {
        if (this.wakeLock === sentinel) this.wakeLock = null;
      });
    } catch {
      // Denied (battery saver, unsupported, not visible): recording works without it.
    } finally {
      this.wakeLockPending = false;
    }
  }

  private releaseWakeLock(): void {
    const sentinel = this.wakeLock;
    this.wakeLock = null;
    if (sentinel) void sentinel.release().catch(() => {});
  }
}

function stopHeartbeat(s: Session): void {
  if (s.heartbeat !== null) clearInterval(s.heartbeat);
  s.heartbeat = null;
}

/**
 * Recordings that were not saved: crash-recovery copies of recordings that did not finish (or whose
 * upload the server refused), plus refused uploads the outbox had to keep itself. Recordings that
 * are still in use – being recorded, or uploaded from memory, in this or another tab – and copies
 * whose upload is still queued are left out.
 */
export async function listRecoverableRecordings(): Promise<RecoverableRecording[]> {
  const records = await listRecordingRecords().catch((err: unknown) => {
    reportStorageError(err);
    return [] as RecordingRecord[];
  });
  const [inUse, queued, parked] = await Promise.all([recordingsInUse(), queuedRecoveryIds(), parkedUploads()]);
  const result: RecoverableRecording[] = [];
  const cutoff = new Date(Date.now() - EMPTY_RECORD_GRACE_MS).toISOString();
  for (const r of records) {
    if (inUse.has(r.id) || queued.has(r.id)) continue;
    if (r.bytes <= 0) {
      // Started but never produced data (e.g. closed within the first second): clean up.
      if (r.updatedAt < cutoff) void deleteRecordingData(r.id).catch(reportStorageError);
      continue;
    }
    result.push({
      id: r.id,
      startedAt: r.startedAt,
      updatedAt: r.updatedAt,
      mimeType: r.mimeType,
      durationMs: r.durationMs,
      bytes: r.bytes,
      profile: r.profile,
      meta: r.meta,
    });
  }
  for (const p of parked) {
    if (inUse.has(p.id) || queued.has(p.id)) continue;
    const body = (typeof p.op.body === 'object' && p.op.body !== null ? p.op.body : {}) as Record<string, unknown>;
    result.push({
      id: p.id,
      startedAt: typeof body.createdAt === 'string' ? body.createdAt : p.op.createdAt,
      updatedAt: p.op.createdAt,
      mimeType: p.op.blobMimeType || p.op.blob?.type || '',
      durationMs: typeof body.durationMs === 'number' ? body.durationMs : 0,
      bytes: p.op.blob?.size ?? 0,
      profile: body.diarize === true ? 'meeting' : 'dictation',
      meta: { docId: p.op.docId, clipId: p.op.clipId, rejected: true, reason: p.reason, ...(p.op.userId ? { userId: p.op.userId } : {}) },
    });
  }
  return result.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

export async function recoverRecording(id: string): Promise<RecordingResult & { meta: Record<string, unknown> }> {
  // Another tab is still recording (or uploading) it: what is stored is incomplete or about to go.
  if (!isRecordingHeldHere(id) && (await recordingsInUse()).has(id)) throw new Error(MSG_IN_USE);
  let result: RecordingResult & { meta: Record<string, unknown> };
  if (isParkedId(id)) {
    const parked = await parkedUpload(id);
    if (!parked?.op.blob) throw new Error(MSG_RECOVERY_MISSING);
    const body = (typeof parked.op.body === 'object' && parked.op.body !== null ? parked.op.body : {}) as Record<string, unknown>;
    const mimeType = parked.op.blobMimeType || parked.op.blob.type || 'audio/webm';
    result = {
      blob: parked.op.blob.type === mimeType ? parked.op.blob : new Blob([parked.op.blob], { type: mimeType }),
      mimeType,
      durationMs: typeof body.durationMs === 'number' ? body.durationMs : 0,
      recoveryId: id,
      startedAt: typeof body.createdAt === 'string' ? body.createdAt : parked.op.createdAt,
      meta: {
        docId: parked.op.docId,
        clipId: parked.op.clipId,
        rejected: true,
        reason: parked.reason,
        ...(parked.op.userId ? { userId: parked.op.userId } : {}),
      },
    };
  } else {
    const record = await getRecordingRecord(id);
    if (!record) throw new Error(MSG_RECOVERY_MISSING);
    const chunks = await getRecordingChunks(id);
    if (chunks.length === 0) throw new Error('Shranjeni posnetek je prazen.');
    chunks.sort((a, b) => a.index - b.index);
    const mimeType = record.mimeType || chunks[0]!.blob.type || 'audio/webm';
    const blob = new Blob(
      chunks.map((c) => c.blob),
      { type: mimeType },
    );
    result = { blob, mimeType, durationMs: record.durationMs, recoveryId: id, startedAt: record.startedAt, meta: record.meta };
  }
  // Lets api.addAudioClip link a new upload of this audio to its recovery copy.
  rememberRecordingBlob(result.blob, id);
  return result;
}

/**
 * Delete a recovery copy once its audio is safe elsewhere (queued in the outbox) or unwanted.
 * A copy this tab still needs – its upload could only be kept in memory – is kept; the outbox
 * deletes it once the server accepted the upload. Rejects when another tab still uses it.
 */
export async function discardRecording(id: string): Promise<void> {
  if (isRecordingHeldHere(id)) return;
  if ((await recordingsInUse()).has(id)) throw new Error(MSG_IN_USE);
  await discardRecovery(id);
}
